import { Icon, StepperWC } from '@pypestream/design-system';
import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { useSolutionWizardForm } from '../../components/modals/create-solution-modal/use-solution-wizard-form';
import { useSolutionNameValidation } from '../../hooks';
import {
  ACCEPTED_FILE_TYPES,
  formatFileSize,
  getFileIcon,
} from '../../hooks/use-knowledge-agent-upload';
import prostudioMark from '../../assets/home/prostudio-mark.svg?url';
import {
  requestTalkTurn,
  type Decision,
  type PlanItem,
  type PlanKey,
  type TalkMessage,
  type TalkTurn,
} from './talk-api';
import { markWantsVoice } from './wants-voice';
import { useSingleFlight } from './use-single-flight';
import { clearTalk, loadTalk, saveTalk } from './saved-talk';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { tableStore } from '../../stores/table-store';
import {
  canCreateWithCredentials,
  pickLlmCredentialId,
} from '../llm-credential';
import {
  markPendingKnowledgeSetup,
  readKeyFile,
  type KeyFile,
} from './knowledge-keys';

import './talk-it-through.scss';

/**
 * "Talk it through" (prototype `renderTalk`, `renderPlan`, `askFoot`): Start's
 * conversation. The opening question is the page's title over one prompt
 * box; after the first answer it's a centred conversation with the box
 * pinned below, and the plan beside it — the fixed list every solution needs,
 * ticked off as the conversation covers it. The last few decisions come in a
 * question card tucked behind the box; once they're chosen, the box becomes
 * Create.
 *
 * Each turn is the model's (`bobcat/src/server-only/ai/onboarding.ts`): what it took
 * from the answer, the next question and a suggested answer, the plan, and
 * the draft. Create goes through the wizard's own `createSolution`, and what
 * "Add references" attaches becomes the Knowledge Agent's documents.
 */

type Item =
  | { kind: 'you'; text: string }
  | { kind: 'ai'; text: string }
  | { kind: 'asked'; text: string }
  | { kind: 'work'; steps: string[]; secs: number };

const PLAN_LABEL: Record<PlanKey, string> = {
  does: 'Who it’s for and what it does',
  asks: 'Who it serves and what they ask',
  channels: 'Channels',
  tone: 'Tone',
  never: 'What it must never do',
  knowledge: 'Knowledge source',
  verify: 'Customer check',
  handoff: 'Live-agent handoff',
  name: 'Name',
};

const FIRST_EXAMPLE =
  'We’re Harbor Mutual, a regional auto and home insurer. Answer questions about our policies, check where a claim stands, and hand anything it can’t resolve to a live agent with a summary. Friendly and plain, on our website and by phone.';

/** The wizard's model settings, unchanged: this path doesn't expose them. */
const MODEL_DEFAULTS = {
  temperature: 0.2,
  maxOutputTokens: 2048,
  topP: 0.8,
  topK: 0.2,
};

const SendIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 19V5" />
    <path d="M5 12l7-7 7 7" />
  </svg>
);

/** The transcript the model reads: what was said, not how it was shown. */
const toMessages = (items: Item[]): TalkMessage[] =>
  items.flatMap((i): TalkMessage[] =>
    i.kind === 'you'
      ? [{ role: 'user', text: i.text }]
      : i.kind === 'ai' || i.kind === 'asked'
        ? [{ role: 'assistant', text: i.text }]
        : []
  );

/** A textarea that grows with what's typed, up to its CSS max-height. */
const grow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
};

const Plan: FC<{
  plan: PlanItem[];
  working: boolean;
  open: boolean;
}> = ({ plan, working, open }) => {
  const done = plan.filter((p) => p.done).length;
  const now = working ? -1 : plan.findIndex((p) => !p.done);
  return (
    <aside
      className={`c-talk__plan${open ? ' is-open' : ''}`}
      aria-label="Plan"
      data-testid="bobcat-talk-plan"
    >
      <div className="c-talk__plan-head">
        <span className="c-talk__plan-title">Plan</span>
        <span className="c-talk__plan-count">
          {done} of {plan.length}
        </span>
      </div>
      <div className="c-talk__plan-bar">
        <i style={{ width: `${Math.round((done / plan.length) * 100)}%` }} />
      </div>
      <ol className="c-talk__plan-list">
        {plan.map((p, i) => (
          <li
            key={p.key}
            className={`c-talk__plan-item${
              p.done ? ' is-done' : i === now ? ' is-now' : ''
            }`}
          >
            <span className="c-talk__plan-mark">
              {p.done && <Icon name="check" size="xsmall" />}
            </span>
            <span className="c-talk__plan-body">
              <span>{PLAN_LABEL[p.key]}</span>
              {p.done && p.value ? (
                <span className="c-talk__plan-value">{p.value}</span>
              ) : i === now ? (
                <span className="c-talk__plan-value">Asking now</span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
      <p className="c-talk__plan-note">
        <Icon name="info" size="xsmall" />
        <span>
          Documents it should answer from can go in with Add references, or
          later on the Knowledge Agent.
        </span>
      </p>
    </aside>
  );
};

const EMPTY_PLAN: PlanItem[] = (Object.keys(PLAN_LABEL) as PlanKey[]).map(
  (key) => ({ key, done: false })
);

export const TalkItThrough: FC<{
  firstName?: string;
  onBack: () => void;
}> = ({ firstName, onBack }) => {
  // The wizard's create path without its stepper: nothing here to move.
  const noStepper = useRef<StepperWC>(null);
  const {
    createSolution,
    credentials,
    isLoading,
    setIsLoading,
    isBotFlowError,
    uploadedFiles,
    addFile,
    removeFile,
  } = useSolutionWizardForm(noStepper);

  const canCreate = canCreateWithCredentials(credentials);
  // Not while the project's providers are still loading.
  const showNoProvider = !!credentials && !canCreate;
  const noProviderId = useId();

  // Company first (Edan's review, Oct 27): the assistant speaks for a brand.
  const opening = `Hey${firstName ? ` ${firstName}` : ''}, which company is this for, and what should it do for your customers?`;
  // A conversation left part-way through comes back (`saved-talk.ts`).
  const ctxProjectId = useProStudioCtxSelector((ctx) => ctx.project?.id);
  const projectId =
    ctxProjectId ?? window.location.pathname.split('/').filter(Boolean)[0];
  const [saved] = useState(() => loadTalk<Item>(projectId ?? ''));
  const [items, setItems] = useState<Item[]>(saved?.items ?? []);
  const [turn, setTurn] = useState<TalkTurn | null>(saved?.turn ?? null);
  const [draft, setDraftText] = useState('');
  const [working, setWorking] = useState<{ t0: number } | null>(null);
  const [now, setNow] = useState(Date.now());
  const [problem, setProblem] = useState<string | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [openWork, setOpenWork] = useState<number | null>(null);

  // The question card: the decisions offered when the conversation's open
  // questions ran out, held still while they're answered.
  const [asks, setAsks] = useState<Decision[]>(saved?.asks ?? []);
  const [askAt, setAskAt] = useState(saved?.askAt ?? 0);
  const [askSel, setAskSel] = useState(0);
  const [chosen, setChosen] = useState<Partial<Record<PlanKey, string>>>(
    saved?.chosen ?? {}
  );
  const [asksSent, setAsksSent] = useState(saved?.asksSent ?? false);

  // The first step: the service-account keys the solution's scripts load
  // (`knowledge-keys.ts`). Skippable; the conversation starts after it.
  const [keys, setKeys] = useState<KeyFile[]>([]);
  const [corpusKey, setCorpusKey] = useState(0);
  const [keysDone, setKeysDone] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  const keyInput = useRef<HTMLInputElement>(null);
  const addKeyFiles = async (list: FileList | null) => {
    setKeyError(null);
    const read = await Promise.all(
      [...(list ?? [])].map(async (f) => readKeyFile(f.name, await f.text()))
    );
    const bad = read.find((r): r is { error: string } => 'error' in r);
    if (bad) setKeyError(bad.error);
    const good = read.filter((r): r is KeyFile => !('error' in r));
    setKeys((prev) => [
      ...prev,
      ...good.filter((g) => !prev.some((p) => p.name === g.name)),
    ]);
  };

  const [attempt, setAttempt] = useState(0);
  const [createFailed, setCreateFailed] = useState(false);
  // `createOnce` waits out `absorb()` before `createSolution` sets
  // `isLoading`; a second click in that wait made a second solution, and the
  // conversation could still change under the draft it had already read.
  const [create, starting] = useSingleFlight(() => createOnce());
  const [title, setTitle] = useState(saved?.title ?? '');

  // Kept as it changes; an empty conversation clears what was kept.
  useEffect(() => {
    if (!projectId) return;
    saveTalk(projectId, { items, turn, chosen, asks, askAt, asksSent, title });
  }, [projectId, items, turn, chosen, asks, askAt, asksSent, title]);
  const { isValidName, validateSolutionName } =
    useSolutionNameValidation(title);

  const scroll = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const files = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  // The working line's timer.
  useEffect(() => {
    if (!working) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(t);
  }, [working]);

  // New messages land at the bottom, in view.
  useLayoutEffect(() => {
    scroll.current?.scrollTo({
      top: scroll.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [items, working, askAt, turn?.ready]);

  // The create failed (the machine lands in `botFlowError`): say so.
  useEffect(() => {
    if (!attempt || !isBotFlowError) return;
    setIsLoading(false);
    setCreateFailed(true);
    // Put the conversation back where it was drawn in from.
    scroll.current
      ?.querySelectorAll<HTMLElement>('.c-talk__thread > *')
      .forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
  }, [attempt, isBotFlowError, setIsLoading]);

  // The title comes from the draft, until they edit it.
  useEffect(() => {
    if (turn?.draft.title) setTitle(validateSolutionName(turn.draft.title));
    // Only when the draft's title changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn?.draft.title]);

  const run = useCallback(
    async (next: Item[], decisions: Partial<Record<PlanKey, string>>) => {
      setItems(next);
      setProblem(null);
      setWorking({ t0: Date.now() });
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      const t0 = Date.now();
      try {
        const result = await requestTalkTurn(
          {
            // Not the designer's name: the opening line already greets them,
            // and a model given a name uses it in every reply.
            messages: toMessages(next),
            references: uploadedFiles.map((f) => f.name),
            decisions,
          },
          ctrl.signal
        );
        if (ctrl.signal.aborted) return;
        const secs = Math.max(1, Math.round((Date.now() - t0) / 1000));
        setItems([
          ...next,
          ...(result.work.length
            ? [{ kind: 'work' as const, steps: result.work, secs }]
            : []),
          { kind: 'ai', text: result.reply },
        ]);
        setTurn(result);
        if (!result.next && result.decisions.length && !result.ready) {
          setAsks(result.decisions);
          setAskAt(0);
          setAskSel(0);
          setAsksSent(false);
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setItems(next);
        setProblem(
          `Couldn’t get a reply: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        if (!ctrl.signal.aborted) setWorking(null);
      }
    },
    [uploadedFiles]
  );

  const deciding = asks.length > 0 && !asksSent && !turn?.ready;
  const suggestion = turn ? (turn.next?.example ?? '') : FIRST_EXAMPLE;

  const answerDecision = (value: string) => {
    const x = asks[askAt];
    if (!x || !value.trim() || working) return;
    const nextChosen = { ...chosen, [x.key]: value.trim() };
    setChosen(nextChosen);
    setDraftText('');
    if (askAt < asks.length - 1) {
      setAskAt(askAt + 1);
      setAskSel(0);
      return;
    }
    // All chosen: they go into the conversation together, as one reply.
    setAsksSent(true);
    run(
      [
        ...items,
        ...asks.flatMap((a): Item[] => [
          { kind: 'asked', text: a.question },
          { kind: 'you', text: nextChosen[a.key] ?? '' },
        ]),
      ],
      nextChosen
    );
  };

  const send = (raw?: string) => {
    if (working || isLoading || starting) return;
    if (deciding) {
      answerDecision(raw ?? draft);
      return;
    }
    const text = (raw ?? draft).trim();
    if (!text) return;
    setDraftText('');
    run([...items, { kind: 'you', text }], chosen);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab puts the suggestion in the box, ready to send.
    if (e.key === 'Tab' && !e.shiftKey && !draft && suggestion && !deciding) {
      e.preventDefault();
      setDraftText(suggestion);
      window.requestAnimationFrame(() => grow(box.current));
      return;
    }
    if (deciding && !draft) {
      const opts = asks[askAt]?.options ?? [];
      const n = Number(e.key);
      if (n >= 1 && n <= opts.length) {
        e.preventDefault();
        answerDecision(opts[n - 1]);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const d = e.key === 'ArrowDown' ? 1 : -1;
        setAskSel((s) => (s + d + opts.length) % opts.length);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        answerDecision(opts[askSel] ?? '');
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const createBtn = useRef<HTMLButtonElement>(null);

  /**
   * The conversation is drawn into the Create button (prototype
   * `createFromChat`): only what's on screen moves, nearest first, so it
   * reads as being pulled in. Resolves when it's done; at once where motion
   * is reduced.
   */
  const absorb = () =>
    new Promise<void>((resolve) => {
      const btn = createBtn.current;
      const sc = scroll.current;
      if (
        !btn ||
        !sc ||
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ) {
        resolve();
        return;
      }
      const b = btn.getBoundingClientRect();
      const cx = b.left + b.width / 2;
      const cy = b.top + b.height / 2;
      const area = sc.getBoundingClientRect();
      const msgs = [...sc.querySelectorAll<HTMLElement>('.c-talk__thread > *')]
        .filter((el) => {
          const r = el.getBoundingClientRect();
          return r.bottom > area.top && r.top < area.bottom;
        })
        .reverse();
      const FLY = 520;
      const GAP = msgs.length > 1 ? Math.min(45, 320 / (msgs.length - 1)) : 0;
      msgs.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const dx = cx - (r.left + r.width / 2);
        const dy = cy - (r.top + r.height / 2);
        el.animate(
          [
            { transform: 'none', opacity: 1 },
            {
              transform: `translate(${dx}px, ${dy}px) scale(0.12)`,
              opacity: 0,
            },
          ],
          {
            duration: FLY,
            delay: i * GAP,
            easing: 'cubic-bezier(.55, 0, .8, .2)',
            fill: 'forwards',
          }
        );
      });
      const total = (msgs.length ? (msgs.length - 1) * GAP : 0) + FLY;
      btn.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.03)', offset: 0.8 },
          { transform: 'scale(1)' },
        ],
        { duration: total, easing: 'ease-in-out' }
      );
      window.setTimeout(resolve, total);
    });

  const createOnce = async () => {
    if (!turn || isLoading || !isValidName || !canCreate) return;
    setCreateFailed(false);
    await absorb();
    const d = turn.draft;
    // Demo only (`FE_BOBCAT_DEMO_CORPUS_ID`): every new solution searches one
    // existing knowledge base, so the references are shown but neither
    // uploaded nor built into a new one.
    const demoCorpus = !!import.meta.env.FE_BOBCAT_DEMO_CORPUS_ID?.trim();
    const references = demoCorpus
      ? []
      : uploadedFiles.map((f) => f.file).filter((f): f is File => !!f);
    createSolution({
      // With keys, the references are built into the knowledge base here
      // (`run-knowledge-setup.ts`), not sent to the backend upload.
      ...(keys.length || demoCorpus ? { files: [] } : {}),
      formData: {
        botName: title,
        type: 'supervisor',
        llmCredentialId: pickLlmCredentialId(credentials),
        supervisorName: d.supervisorName || title,
        brandName: d.brandName || undefined,
        tone: d.tone || undefined,
        // The global instructions: where Design shows them and "Edit
        // solution → Knowledge Agent" edits them.
        knowledgeAgentContext: d.instructions || undefined,
        otherInformation: d.instructions || undefined,
        tasks: d.tasks.map((t) => ({
          name: t.name,
          description: t.phrases.join(' | '),
        })),
        ...MODEL_DEFAULTS,
      },
      callback: () => undefined,
      // Creating makes Pype channels only; Design offers Voice when asked for.
      onBeforeNavigate: (uniqueId) => {
        // Created: the conversation is the solution now, not one to resume.
        if (projectId) clearTalk(projectId);
        if (d.channels.voice) markWantsVoice(uniqueId);
        // Each task becomes a real agent once the solution has loaded: from
        // a template when one fits, otherwise drafted by AI from the brief
        // (agent templates; the store checks the master).
        tableStore.markPendingAgentDrafts(
          uniqueId,
          [
            d.instructions,
            ...toMessages(items)
              .filter((m) => m.role === 'user')
              .map((m) => m.text),
          ]
            .filter(Boolean)
            .join('\n\n'),
          d.tasks.map((t) => ({
            name: t.name,
            phrases: t.phrases,
            ...(t.template ? { template: t.template } : {}),
          }))
        );
        if (keys.length) {
          markPendingKnowledgeSetup(uniqueId, {
            keys,
            corpusKey,
            references,
            title,
          });
        }
      },
    });
    setAttempt((n) => n + 1);
  };

  const plan = turn?.plan ?? EMPTY_PLAN;
  const planDone = plan.filter((p) => p.done).length;
  const hero = items.length === 0 && !working;
  const secs = working ? Math.max(1, Math.round((now - working.t0) / 1000)) : 0;

  const refs = (
    <div className="c-talk__refs">
      <button
        type="button"
        className="c-talk__refs-add"
        data-testid="bobcat-talk-add-references"
        disabled={isLoading || starting}
        onClick={() => files.current?.click()}
      >
        <Icon name="plus" size="xsmall" />
        Add references
      </button>
      <input
        ref={files}
        type="file"
        multiple
        hidden
        accept={ACCEPTED_FILE_TYPES.join(',')}
        onChange={(e) => {
          [...(e.currentTarget.files ?? [])].forEach(addFile);
          e.currentTarget.value = '';
        }}
      />
    </div>
  );

  // The solution's references, above the box rather than in it: they belong
  // to the knowledge base, not to the next message, and each turn sends only
  // their names.
  const chips = uploadedFiles.length > 0 && (
    <ul
      className="c-talk__ref-chips"
      aria-label="References for the knowledge base"
    >
      <li className="c-talk__ref-label">Knowledge base</li>
      {uploadedFiles.map((f) => (
        <li
          key={f.id}
          className="c-talk__ref"
          title={`${f.name} · ${formatFileSize(f.size)}`}
        >
          <Icon name={getFileIcon(f.name)} size="xsmall" />
          <span className="c-talk__ref-name">{f.name}</span>
          <button
            type="button"
            className="c-talk__ref-x"
            aria-label={`Remove ${f.name}`}
            onClick={() => removeFile(f.id)}
          >
            <Icon name="close" size="xsmall" />
          </button>
        </li>
      ))}
    </ul>
  );

  const composer = (
    <div className="c-talk__refs-wrap">
      {chips}
      {!deciding && refs}
      <div
        className={`c-talk__composer${hero ? ' c-talk__composer--hero' : ''}`}
      >
        <textarea
          ref={box}
          rows={1}
          value={draft}
          disabled={!!working || isLoading || starting}
          placeholder={
            working
              ? 'Working on it…'
              : deciding
                ? 'Or type your own answer'
                : suggestion
          }
          aria-label="Your answer"
          data-testid="bobcat-talk-input"
          onChange={(e) => {
            setDraftText(e.currentTarget.value);
            grow(e.currentTarget);
          }}
          onKeyDown={onKeyDown}
          autoFocus
        />
        <div className="c-talk__composer-row">
          <span className="c-talk__hint">
            {working ? null : deciding ? (
              <>
                <kbd>1</kbd>–<kbd>{asks[askAt]?.options.length ?? 3}</kbd> or{' '}
                <kbd>↑</kbd>
                <kbd>↓</kbd> to choose · <kbd>Enter</kbd> to answer
              </>
            ) : suggestion ? (
              <>
                <kbd>Tab</kbd> to use the suggestion
                {hero && (
                  <>
                    {' '}
                    · <kbd>Shift</kbd> + <kbd>Enter</kbd> for a new line
                  </>
                )}
              </>
            ) : null}
          </span>
          <button
            type="button"
            className="c-talk__send"
            aria-label="Send"
            data-testid="bobcat-talk-send"
            disabled={!!working || !draft.trim()}
            onClick={() => send()}
          >
            <SendIcon />
          </button>
        </div>
      </div>
    </div>
  );

  const askCard = deciding && asks[askAt] && (
    <div className="c-talk__ask-card" data-testid="bobcat-talk-ask">
      <div className="c-talk__ask-body" key={asks[askAt].key}>
        <div className="c-talk__ask-head">
          <span>
            Question {askAt + 1} of {asks.length}
          </span>
          <span className="c-talk__ask-nav">
            <button
              type="button"
              aria-label="Previous question"
              disabled={askAt === 0}
              onClick={() => setAskAt(askAt - 1)}
            >
              <Icon name="chevron-left" size="xsmall" />
            </button>
            <button
              type="button"
              aria-label="Next question"
              disabled={!chosen[asks[askAt].key] || askAt >= asks.length - 1}
              onClick={() => setAskAt(askAt + 1)}
            >
              <Icon name="chevron-right" size="xsmall" />
            </button>
          </span>
        </div>
        <p className="c-talk__ask-q">{asks[askAt].question}</p>
        {asks[askAt].why && (
          <p className="c-talk__ask-why">{asks[askAt].why}</p>
        )}
        <div className="c-talk__ask-opts">
          {asks[askAt].options.map((o, i) => (
            <button
              key={o}
              type="button"
              className={`c-talk__ask-opt${i === askSel ? ' is-sel' : ''}`}
              onMouseEnter={() => setAskSel(i)}
              onClick={() => answerDecision(o)}
            >
              <span className="c-talk__ask-num">{i + 1}</span>
              <span className="c-talk__ask-label">{o}</span>
              {i === 0 && <span className="c-talk__ask-rec">Recommended</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const foot = turn?.ready ? (
    <div className="c-talk__create">
      {!isValidName && (
        <label className="c-talk__rename">
          <span>
            {title.trim()
              ? `A solution called “${title}” already exists. Name this one:`
              : 'Name this solution:'}
          </span>
          <input
            value={title}
            onChange={(e) =>
              setTitle(validateSolutionName(e.currentTarget.value))
            }
          />
        </label>
      )}
      {createFailed && (
        <p className="c-talk__problem" role="alert">
          Couldn’t create the solution. Try again in a moment — if it keeps
          failing, the error is in the notifications.
        </p>
      )}
      <button
        ref={createBtn}
        type="button"
        className="c-talk__create-btn"
        data-testid="bobcat-talk-create"
        disabled={isLoading || starting || !isValidName || !canCreate}
        aria-describedby={showNoProvider ? noProviderId : undefined}
        onClick={create}
      >
        {isLoading || starting ? 'Creating…' : 'Create solution'}
      </button>
      {showNoProvider && (
        <p className="c-talk__problem" id={noProviderId}>
          Connect an AI provider first.
        </p>
      )}
    </div>
  ) : (
    <div className="c-talk__ask">
      {askCard}
      {composer}
    </div>
  );

  const top = (
    <div className="c-talk__top">
      <button
        type="button"
        className="c-talk__back"
        onClick={() => {
          if (projectId) clearTalk(projectId);
          onBack();
        }}
      >
        <Icon name="arrow-left" size="xsmall" />
        Ways to start
      </button>
      <button
        type="button"
        className="c-talk__plan-toggle"
        aria-expanded={planOpen}
        onClick={() => setPlanOpen((o) => !o)}
      >
        <Icon name="clipboard" size="xsmall" />
        Plan · {planDone} of {plan.length}
      </button>
    </div>
  );

  return (
    <div className="c-talk" data-testid="bobcat-talk">
      <div className="c-talk__main">
        {top}
        {hero && !keysDone ? (
          <div className="c-talk__hero" data-testid="bobcat-talk-keys">
            {/* Optional (Edan's review, Oct 27): the agents run on the AI
                Gateway, so these files only give the Knowledge Agent a RAG
                engine in the customer's own Google Cloud project. */}
            <h1 className="c-talk__hero-title">
              Create a new RAG engine in Google Cloud
            </h1>
            <p className="c-talk__keys-lead">
              Optional. Your agents already run on Pypestream&apos;s AI Gateway,
              so there&apos;s nothing to connect for them. Add a credential file
              only if the Knowledge Agent should search your documents in your
              own Google Cloud project.
            </p>
            <div className="c-talk__keys">
              {keys.length > 0 && (
                <ul className="c-talk__keys-list" aria-label="Credentials">
                  {keys.map((k, i) => (
                    <li key={k.name} className="c-talk__key">
                      <Icon name="lock" size="small" />
                      <span className="c-talk__ref-body">
                        <span className="c-talk__ref-name">{k.name}</span>
                        <span className="c-talk__ref-meta">{k.projectId}</span>
                      </span>
                      {keys.length > 1 && (
                        <label className="c-talk__key-kb">
                          <input
                            type="radio"
                            name="corpus-key"
                            checked={corpusKey === i}
                            onChange={() => setCorpusKey(i)}
                          />
                          Knowledge base here
                        </label>
                      )}
                      <button
                        type="button"
                        className="c-talk__ref-x"
                        aria-label={`Remove ${k.name}`}
                        onClick={() => {
                          setKeys((prev) => prev.filter((p) => p !== k));
                          setCorpusKey(0);
                        }}
                      >
                        <Icon name="close" size="xsmall" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button
                type="button"
                className="c-talk__keys-add"
                data-testid="bobcat-talk-add-keys"
                onClick={() => keyInput.current?.click()}
              >
                <Icon name="plus" size="xsmall" />
                {keys.length
                  ? 'Add another credential file'
                  : 'Add credential files'}
              </button>
              <input
                ref={keyInput}
                type="file"
                multiple
                hidden
                accept=".py,.json,application/json"
                onChange={(e) => {
                  void addKeyFiles(e.currentTarget.files);
                  e.currentTarget.value = '';
                }}
              />
              {keyError && <p className="c-talk__keys-error">{keyError}</p>}
            </div>
            <div className="c-talk__keys-actions">
              <button
                type="button"
                className="c-talk__create-btn c-talk__keys-continue"
                data-testid="bobcat-talk-keys-continue"
                onClick={() => setKeysDone(true)}
              >
                {keys.length ? 'Continue' : 'Continue without'}
              </button>
            </div>
          </div>
        ) : hero ? (
          <div className="c-talk__hero">
            <h1 className="c-talk__hero-title">{opening}</h1>
            {composer}
            <p className="c-talk__hero-note">
              A line or a detailed brief. We draft the agents from it, ask about
              anything missing, then the few decisions left.
            </p>
          </div>
        ) : (
          <>
            <div className="c-talk__scroll" ref={scroll}>
              <div className="c-talk__thread">
                <div className="c-talk__msg c-talk__msg--ai">
                  <div className="c-talk__text">{opening}</div>
                </div>
                {items.map((item, i) => {
                  if (item.kind === 'work') {
                    const open = openWork === i;
                    return (
                      // The transcript only grows; its order is its identity.
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={i} className="c-talk__work">
                        <button
                          type="button"
                          className="c-talk__work-toggle"
                          aria-expanded={open}
                          onClick={() => setOpenWork(open ? null : i)}
                        >
                          Worked for {item.secs}s
                          <Icon name="chevron-right" size="xsmall" />
                        </button>
                        {open && (
                          <ul className="c-talk__work-steps">
                            {item.steps.map((s) => (
                              <li key={s}>
                                <Icon name="check" size="xsmall" />
                                {s}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  }
                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      className={`c-talk__msg c-talk__msg--${item.kind}`}
                    >
                      {item.kind === 'you' ? (
                        <div className="c-talk__bubble">{item.text}</div>
                      ) : (
                        <div className="c-talk__text">{item.text}</div>
                      )}
                    </div>
                  );
                })}
                {working && (
                  <div className="c-talk__live" role="status">
                    <img src={prostudioMark} alt="" width={20} height={20} />
                    <span className="c-talk__live-now">
                      {turn ? 'Updating the draft…' : 'Reading your answer…'}
                    </span>
                    <span className="c-talk__live-meta">{secs}s</span>
                  </div>
                )}
                {turn?.ready && turn.draft.agents.length > 0 && (
                  <div className="c-talk__pills" aria-label="Drafted agents">
                    {[
                      'Supervisor Agent',
                      'Knowledge Agent',
                      ...turn.draft.agents,
                    ].map((a) => (
                      <span key={a} className="c-talk__pill">
                        {a}
                      </span>
                    ))}
                  </div>
                )}
                {problem && (
                  <p className="c-talk__problem" role="alert">
                    {problem}{' '}
                    <button
                      type="button"
                      className="c-talk__retry"
                      onClick={() => run(items, chosen)}
                    >
                      Try again
                    </button>
                  </p>
                )}
              </div>
            </div>
            <div className="c-talk__foot">{foot}</div>
          </>
        )}
      </div>
      <Plan plan={plan} working={!!working} open={planOpen} />
    </div>
  );
};
